// CoordinationTabBar - the Coordination group of the main drawer as a tab
// strip pinned right under the main header, on every page of the
// coordination suite.
//
// The drawer is a slide-out: every hop between Issues, Checklists, Areas,
// Review Center... costs a hamburger click, and a per-page side column
// vanished as soon as you left the landing. This bar is mounted ONCE in
// App.jsx (after <Header/>) and shows itself only on coordination routes,
// so the suite reads as one workspace wherever you are in it. It is
// sticky at the top of the viewport while the page scrolls.
//
// Tabs, order and permission gates mirror pages/configuration/newDrawer.jsx
// entry for entry - keep the two in step when adding an entry there.
//
// EACH TAB ON ITS OWN VIEW KEY (owner, 2026-10-14: every coordination module
// CRUD, controllable one by one). Shop drawings, Construction tracking,
// Levels & areas, Technical documentation and Find in sheets were all drawn
// on ganttView; each now shows for its own key (shopDrawingView,
// constructionTrackingView, levelsAreasView, technicalDocsView,
// sheetSearchView). Admins and company admins still see every tab (hasAny
// passes them, usePermissions); partners get these view keys through their
// tier lists (back/utils/partnerPerms READER_PERMS).
// Project-scoped tabs carry the current project (route param first, else
// the remembered coordination project) so a hop lands on the same project.
//
// A PARTNER'S TABS ARE THE OWNER'S PER-PROJECT SWITCHES (owner, 2026-10-15:
// "on the partner page, i don't see how to enable the coordination tabs for
// them"). For a partner account the six coordination tabs (`coordTab` below)
// show exactly when GET /projects/:id/my-coordination-caps says that tab's
// View is on for them on the current project - the same answer the routes
// give - so a tab switched off disappears and one switched on appears, Import
// documents included. Until that answer arrives, or with no project, the
// rule above stands.
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
  DashboardOutlined,
  DiffOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  FlagOutlined,
  ImportOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StarOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { ALL_PERMISSIONS as P } from "constants/permissionsConstants";
import { signoffPagesOn } from "pages/SignoffTimeline/signoffRelease";
import usePermissions from "hooks/usePermissions";
import { getRememberedProject } from "helpers/projectSelection";
import { eventTrack } from "helpers/ga4Helper";
import { getBillableAccess } from "api/billableHours";
import useCoordinationCaps from "hooks/useCoordinationCaps";
import "./CoordinationTabBar.css";

// One tab per drawer entry. `to` may take the current projectId so
// project-scoped pages open on the same project; `match` marks the tab
// active for a pathname.
// OWNER-SIDE TABS ARE NOT SHOWN TO PARTNERS (owner, 2026-10-10: "hide
// those owner tabs for partners too"). A partner firm's own admin passed
// the admin and permission gates below and saw Review Center, Contractor
// hours, Import documents, Analytics and Partners - the GC's tools, not
// theirs. `partnerAccount` comes from the session and uses the same rule
// as the partner email links (back/utils/magicLogin partnerAccountFor).
// A platform admin is never hidden from.
export const hideForPartner = (tab, userInfo) =>
  !!tab.ownerSide && !!userInfo?.partnerAccount && !userInfo?.admin;

// Is this tab drawn? `caps` is the partner's answer for the current project
// (hooks/useCoordinationCaps), null for the project team or while unknown.
export const tabVisible = (tab, ctx, userInfo, caps) => {
  if (caps && caps.tabs && tab.coordTab) return !!(caps.tabs[tab.coordTab] && caps.tabs[tab.coordTab].view);
  return tab.show(ctx) && !hideForPartner(tab, userInfo);
};

const TABS = [
  {
    key: "dashboard",
    label: "Dashboard & Schedule",
    icon: <DashboardOutlined />,
    to: () => "/coordination",
    match: (p) =>
      p === "/coordination" ||
      p.startsWith("/coordination/") ||
      p.startsWith("/scheduler/") ||
      p.startsWith("/plan-builder/") ||
      p.startsWith("/minutes-to-plan"),
    show: ({ hasAny }) => hasAny(P.GANTT_VIEW, P.ISSUE_VIEW),
  },
  {
    // The project's documentation: At a Glance first, then a tab per
    // generated view (2-inch conduit register, roof equipment...).
    key: "documentation",
    coordTab: "technicalDocs",
    label: "Technical documentation",
    icon: <FileTextOutlined />,
    to: (pid) => (pid ? `/documentation/${pid}` : "/documentation"),
    match: (p) => p === "/documentation" || p.startsWith("/documentation/"),
    show: ({ hasAny }) => hasAny(P.TECH_DOCS_VIEW),
  },
  // LEVELS & AREAS IS A TAB AGAIN (owner, 2026-10-10: "rename area tab in
  // coordination, to levels & area" - and every level and area the suite
  // offers now comes from it). It was taken out of the strip on 2026-09-25;
  // the page and route never moved, so every link already sent still lands.
  {
    key: "levels-areas",
    coordTab: "areas",
    // "Levels, Areas & Systems" since 2026-10-13 (owner: "add systems per
    // area, in levels, areas & systems"); the key and route are unchanged.
    label: "Levels, Areas & Systems",
    icon: <AppstoreOutlined />,
    to: (pid) => (pid ? `/areas/${pid}` : "/areas"),
    match: (p) => p === "/areas" || p.startsWith("/areas/") || p === "/volumes" || p.startsWith("/volumes/"),
    show: ({ hasAny, isAdmin }) => !!isAdmin || hasAny(P.AREAS_VIEW),
  },
  // THERE IS NO 3D VOLUMES TAB OR VIEW: the concept was removed (owner 2026-11-05).
  // /volumes links land on Levels, Areas & Systems.
  {
    // SIGN-OFF TIMELINE (owner 2026-10-29): the building by level, area and system,
    // coloured by owner/GC and contractor sign-off on any date, after Levels, Areas &
    // Systems whose areas it is drawn from - and gated the same way.
    key: "signoff-timeline",
    coordTab: "areas",
    label: "Sign-off timeline",
    icon: <StarOutlined />,
    to: (pid) => (pid ? `/signoff-timeline/${pid}` : "/signoff-timeline"),
    match: (p) => p === "/signoff-timeline" || p.startsWith("/signoff-timeline/"),
    show: ({ hasAny, isAdmin }) => signoffPagesOn() && (!!isAdmin || hasAny(P.AREAS_VIEW)),
  },
  {
    // Shop drawings, immediately BEFORE Construction tracking (owner,
    // 2026-09-24). A shorter label than the page title on purpose: the
    // strip has to fit at 1400px with nothing hidden.
    key: "shop-drawings",
    coordTab: "shopDrawings",
    label: "Shop drawings",
    icon: <ApartmentOutlined />,
    to: (pid) => (pid ? `/shop-drawings/${pid}` : "/shop-drawings"),
    match: (p) => p === "/shop-drawings" || p.startsWith("/shop-drawings/"),
    show: ({ hasAny }) => hasAny(P.SHOP_DRAWING_VIEW),
  },
  {
    key: "tracking",
    coordTab: "tracking",
    label: "Construction tracking",
    icon: <CheckSquareOutlined />,
    to: (pid) => (pid ? `/tracking/${pid}` : "/tracking"),
    match: (p) => p === "/tracking" || p.startsWith("/tracking/"),
    show: ({ hasAny }) => hasAny(P.TRACKING_VIEW),
  },
  {
    key: "issues",
    label: "Issues Manager",
    icon: <FlagOutlined />,
    to: () => "/issues",
    match: (p) => p === "/issues" || p.startsWith("/issues/") || p.startsWith("/issue/") || p.startsWith("/issue-viewer/"),
    show: ({ hasAny }) => hasAny(P.ISSUE_VIEW),
    track: "Issues",
  },
  {
    // RFI GENERATOR (owner, 2026-10-20: "as a separate Tab"), right after
    // Issues: an RFI is usually a coordination issue that needs the
    // designer's answer. Partners see it per project on the owner's switch.
    key: "rfi",
    coordTab: "rfi",
    label: "RFI Generator",
    icon: <FileDoneOutlined />,
    to: (pid) => (pid ? `/rfi/${pid}` : "/rfi"),
    match: (p) => p === "/rfi" || p.startsWith("/rfi/"),
    show: ({ hasAny }) => hasAny(P.RFI_VIEW),
  },
  {
    key: "checklists",
    label: "Checklists",
    icon: <SafetyCertificateOutlined />,
    to: (pid) => (pid ? `/checklists?projectId=${pid}` : "/checklists"),
    match: (p) => p === "/checklists" || p.startsWith("/checklists/"),
    show: ({ hasAny }) => hasAny(P.CHECKLIST_VIEW),
  },
  // REVISIONS IS NOT HERE ANY MORE (owner, 2026-09-24): it moved to the
  // Projects group in the drawer. Its ROUTE is unchanged - digests,
  // notifications and mail emit /analytics/revisions - so every link
  // already sent out still lands; only the entry moved. Removing it also
  // paid for the Shop drawings tab below without hiding anything, which
  // was the constraint.
  {
    key: "split-search",
    coordTab: "sheetSearch",
    label: "Find in Sheets & Model",
    icon: <SearchOutlined />,
    to: () => "/split-search",
    match: (p) => p.startsWith("/split-search"),
    show: ({ hasAny }) => hasAny(P.SHEET_SEARCH_VIEW),
  },
  {
    key: "review",
    ownerSide: true,
    label: "Review Center",
    icon: <DiffOutlined />,
    to: () => "/approval-queue",
    match: (p) => p.startsWith("/approval-queue"),
    show: ({ hasAny }) =>
      hasAny(P.REVIEW_CENTER_VIEW, P.REVIEW_CENTER_APPROVE, P.ISSUE_APPROVE_RESOLUTION),
  },
  {
    // CONTRACTOR HOURS AND COST, every billable system. Shown on the
    // server's own answer (GET /billable-hours/access): from 2026-10-10 the
    // page needs contractorsCost, a platform key the SPA's permission lists
    // never carry, and company admin no longer implies it - a tab drawn on
    // `isAdmin` would be a link to a 403 for nearly every admin.
    key: "contractor-hours",
    ownerSide: true,
    label: "Contractor hours & cost",
    icon: <BarChartOutlined />,
    to: () => "/contractor-hours",
    match: (p) => p.startsWith("/contractor-hours"),
    show: ({ contractorsCost }) => !!contractorsCost,
  },
  {
    key: "schedule-import",
    coordTab: "documentImport",
    ownerSide: true,
    label: "Import documents",
    icon: <ImportOutlined />,
    to: (pid) => (pid ? `/schedule-import/${pid}` : "/schedule-import"),
    match: (p) => p === "/schedule-import" || p.startsWith("/schedule-import/"),
    // documentImportView, or ganttAdd - the key this tab was drawn on until
    // 2026-10-14, still a way into the wizard (routes/scheduleImport).
    show: ({ hasAny }) => hasAny(P.DOC_IMPORT_VIEW, P.GANTT_ADD),
  },
  {
    // Analytics Overview + Contributors + Team Performance, one page.
    key: "analytics",
    ownerSide: true,
    label: "Analytics & Performance",
    icon: <BarChartOutlined />,
    to: () => "/analytics",
    match: (p) =>
      p === "/analytics" ||
      p.startsWith("/analytics/teams") ||
      p.startsWith("/analytics/contributors") ||
      p.startsWith("/analytics/project/") ||
      p.startsWith("/analytics/user/") ||
      p === "/team-insights",
    show: ({ hasAny }) => hasAny(P.ANALYTICS_VIEW, P.TEAM_INSIGHTS_VIEW, P.ANALYTICS_CONTRIBUTORS_VIEW),
  },
  {
    // ONE PARTNERS VIEW (owner, 2026-10-16): the project's roster and, for
    // admins, every partner across the account - pages/Partners/PartnersHub.
    // Shown to whoever may see a roster; the account half is admin-only there.
    key: "partners",
    // Still the GC's tab: a partner reaches the roster (read-only) from the
    // Dashboard and Schedule buttons, which open this same view.
    ownerSide: true,
    label: "Partners",
    icon: <TeamOutlined />,
    to: (pid) => (pid ? `/partners?projectId=${pid}` : "/partners"),
    match: (p) => p.startsWith("/partners") || p.startsWith("/partner-management") || p.startsWith("/participants/"),
    show: ({ isAdmin, hasAny }) => isAdmin || hasAny(P.PARTICIPANT_VIEW),
  },
  // REPORTS & NOTIFICATIONS IS NOT A TAB ANY MORE (owner, 2026-09-24:
  // "just keep it on the drawer and remove it from tab view"). The page,
  // the route and every digest are untouched, and the drawer entry
  // stays - with its section's gate widened to the two permissions this
  // tab used to show for, so nobody who reached it from here loses the
  // way in.
];

// Print pages are paper: no chrome on them.
const PRINT = /^\/(schedule-print|gantt-print|tracking-print)\//;

// SUITE PAGES WITH NO TAB OF THEIR OWN. The strip shows itself on
// coordination routes, and it worked out which those were from the tab
// list - so removing a tab silently removed the whole bar from that
// page. Areas is reached from the dashboard and the drawer, and arriving
// there should not cost the way back to Tracking and Issues.
//
// /notifications-console is here for the same reason and it is a REPAIR:
// its tab left on 2026-09-24 (7e5d4486) and nothing has matched that path
// since, so the page has been rendering with no strip at all on
// production. The drawer still reaches it - that part of "keep it on the
// drawer" was checked - but arriving there cost the way back to
// everything else, which nobody asked for and nobody noticed.
//
// The path is /notifications-console, the SPA route. It is not
// /notification-settings: that is the API mount, and they are one letter
// apart in conversation and completely different in a router.
const SUITE_ONLY = [/^\/areas(\/|$)/, /^\/notifications-console(\/|$)/];

// Is this pathname part of the coordination suite at all?
export function isCoordinationPath(pathname) {
  if (PRINT.test(pathname)) return false;
  return TABS.some((t) => t.match(pathname)) || SUITE_ONLY.some((re) => re.test(pathname));
}

// Project id carried in the path of project-scoped suite pages.
const PATH_PROJECT =
  /^\/(coordination|documentation|areas|volumes|signoff-timeline|tracking|scheduler|plan-builder|participants|schedule-import|rfi|analytics\/project)\/([a-f0-9]{24})(?:\/|$)/i;

// The tab list, for the composition tests (the owner's navigation
// changes of 2026-09-24). Exported rather than re-declared in the test,
// so a tab added here cannot be missed there.
export const TABS_FOR_TEST = TABS;

// ONE QUESTION PER SIGNED-IN PERSON, not one per page: the bar mounts on
// every coordination route. A failed answer is "no" (the tab hides), and is
// not cached, so the next page asks again.
const accessByUser = {};
// Exported so the drawer's Coordination group asks the SAME question once per
// person rather than its own (owner, 2026-10-26: "make sure all tab in
// coordination are represented with ethir submenu access on the right side
// drawer menu, under coordination" - this tab is the one that had no entry).
export function contractorsAccess(userId) {
  if (!userId) return Promise.resolve(false);
  if (!accessByUser[userId]) {
    accessByUser[userId] = getBillableAccess()
      .then((r) => !!(r && r.ok))
      .catch(() => { delete accessByUser[userId]; return false; });
  }
  return accessByUser[userId];
}

// ONE LINE, TITLES SHORTENED WITH "..." (owner, 2026-10-21: "resize the
// tabs to fit 1 line, by adding ... on titles, and tooltip"). The CSS lets
// a label shrink; the full title shows on hover only when it is actually cut,
// measured at hover time because the cut changes with the window width. The
// whole label stays in the DOM, so a screen reader reads it in full.
function TabLink({ tab, to, active, onClick }) {
  const labelRef = useRef(null);
  const [cut, setCut] = useState(false);
  const measure = () => {
    const el = labelRef.current;
    setCut(!!el && el.scrollWidth > el.clientWidth + 1);
  };
  return (
    <Tooltip title={cut ? tab.label : ""} placement="bottom" mouseEnterDelay={0.3}>
      <Link
        to={to}
        className={`coord-tabbar__tab${active ? " is-active" : ""}`}
        aria-current={active ? "page" : undefined}
        onMouseEnter={measure}
        onFocus={measure}
        onClick={onClick}
      >
        <span className="coord-tabbar__icon">{tab.icon}</span>
        <span className="coord-tabbar__label" ref={labelRef}>{tab.label}</span>
      </Link>
    </Tooltip>
  );
}

export default function CoordinationTabBar() {
  const perms = usePermissions();
  const { pathname, search } = useLocation();
  const userInfo = useSelector((s) => s.user?.userInfo);
  const userId = userInfo?._id || userInfo?.id;

  // Current project: route param, else ?projectId=, else the remembered
  // coordination project (kept fresh via the event rememberProject fires).
  const [remembered, setRemembered] = useState(() => getRememberedProject(userId));
  useEffect(() => {
    setRemembered(getRememberedProject(userId));
    const onChange = () => setRemembered(getRememberedProject(userId));
    window.addEventListener("bl:project-remembered", onChange);
    return () => window.removeEventListener("bl:project-remembered", onChange);
  }, [userId]);

  const [contractorsCost, setContractorsCost] = useState(false);
  useEffect(() => {
    let live = true;
    contractorsAccess(userId).then((ok) => { if (live) setContractorsCost(ok); });
    return () => { live = false; };
  }, [userId]);

  let projectId = (pathname.match(PATH_PROJECT) || [])[2] || "";
  if (!projectId) {
    try { projectId = new URLSearchParams(search).get("projectId") || ""; } catch (e) { /* ignore */ }
  }
  if (!projectId) projectId = remembered;
  const caps = useCoordinationCaps(projectId);

  if (!isCoordinationPath(pathname)) return null;
  const tabs = TABS.filter((t) => tabVisible(t, { ...perms, contractorsCost }, userInfo, caps));
  if (!tabs.length) return null;

  return (
    <nav className="coord-tabbar" aria-label="Coordination">
      <div className="coord-tabbar__inner">
        <span className="coord-tabbar__title">Coordination</span>
        <ul className="coord-tabbar__list">
          {tabs.map((t) => {
            const active = t.match(pathname);
            return (
              // --tab-n: the title's length, so a short title keeps its
              // whole width and the long ones give up room first (CSS).
              <li key={t.key} className={active ? "is-active" : undefined} style={{ "--tab-n": t.label.length }}>
                <TabLink
                  tab={t}
                  to={t.to(projectId)}
                  active={active}
                  onClick={() => {
                    if (t.track) {
                      eventTrack(
                        `Navigate-${t.track}-Action`,
                        `Navigate-${t.track}`,
                        userInfo?.name,
                        "+1",
                        false,
                        "+1"
                      );
                    }
                  }}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
